import _, {isUndefined, last, uniqBy} from 'lodash';

import {Mutable} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
  AttachmentVisualContentTypesEnum,
  UploadedAttachmentModel,
} from '../../models/api/attachmentModel';
import {
  DraftMessageModel,
  MessageModel,
  MessageTypesEnum,
  MessageVisualStatusesEnum,
  visualTypeFromType,
} from '../../models/api/messageModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {DataState} from '../../store/data/dataState';
import {EmailFormatsEnum} from './emailFormatHelpers';
import {findInlineAttachmentsInHtml} from './messageHelpers';
import {buildRecipientsFromVisual} from './recipientHelpers';
import {selectSignaturesForChannel} from './signatureHelpers';

export interface MessagePatch {
  newType?: MessageTypesEnum;
  newAuthorId?: number;
  addAuthorIds?: ReadonlyArray<number>;
  newChannelId?: number;
  newHtml?: string;
  newText?: string;
  newSubject?: string;
  newRecipients?: ReadonlyArray<VisualRecipientModel>;
  addedAttachments?: ReadonlyArray<UploadedAttachmentModel>;
  removedAttachmentCids?: ReadonlyArray<string>;
  newIsShared?: boolean;
  newCannedAnswerId?: number;

  newQuoteHtml?: string;
  newIsQuoteIncluded?: boolean;
  newWasQuoteModified?: boolean;

  newForwardHtml?: string;
  newIsForwardIncluded?: boolean;
  newWasForwardModified?: boolean;

  newSignatureId?: number;
  newSignatureHtml?: string;
  newIsSignatureIncluded?: boolean;
  newWasSignatureModified?: boolean;

  newFormat?: EmailFormatsEnum | undefined;
  newDefaultFontStyle?: string;
}

interface MessagePatchOptions {
  onlyIfLastSave?: boolean;
}

export type MessagePatcher = (
  data: DataState,
  message: DraftMessageModel,
  options?: MessagePatchOptions,
) => DraftMessageModel | undefined;

export type MessagePatcherFactory = (messagePatch: MessagePatch) => MessagePatcher;

export function buildMessagePatcherFactory(): MessagePatcherFactory {
  let saveStack: ReadonlyArray<MessagePatch> = [];

  return (messagePatch) => {
    saveStack = [...saveStack, messagePatch];

    return (data, message, {onlyIfLastSave}: MessagePatchOptions = {}) => {
      // If this is an unconditionnal patch, just patch and return.
      if (!onlyIfLastSave) {
        return patchMessage(data, message, messagePatch);
      }

      // Otherwise, check if this is the last save.
      if (last(saveStack) !== messagePatch) {
        return undefined;
      }

      // If so, patch the whole stack sequentially and reset it.
      const patchedMessage = saveStack.reduce((m, o) => patchMessage(data, m, o), message);
      saveStack = [];
      return patchedMessage;
    };
  };
}

function patchMessage(
  data: DataState,
  message: DraftMessageModel,
  messagePatch: MessagePatch,
): DraftMessageModel {
  // Validate that this message is a draft.
  const {visualStatus, updatedAt} = message;
  if (visualStatus !== MessageVisualStatusesEnum.DRAFT) {
    throw new Error('Only draft messages can be updated.');
  }

  // Create an empty message to merge.
  const updatedMessage: Mutable<MessageModel> = {
    ...message,
    isSaving: true,
    visualStatus,
    updatedAt,
  };

  if (!isUndefined(messagePatch.newType)) {
    updatedMessage.type = messagePatch.newType;
    updatedMessage.visualType = visualTypeFromType(updatedMessage.type);
  }

  if (messagePatch.newAuthorId) {
    updatedMessage.authorId = messagePatch.newAuthorId;
  }

  if (!isUndefined(messagePatch.addAuthorIds)) {
    updatedMessage.addAuthorIds = _.chain([...(message.addAuthorIds ?? []), ...messagePatch.addAuthorIds])
      .sortBy()
      .sortedUniq()
      .value();
  }

  if (!isUndefined(messagePatch.newChannelId)) {
    updatedMessage.fromChannelId = messagePatch.newChannelId;
  }

  if (messagePatch.newIsShared) {
    updatedMessage.isShared = true;
  }

  if (!isUndefined(messagePatch.newHtml)) {
    updatedMessage.html = messagePatch.newHtml;
  }

  if (!isUndefined(messagePatch.newText)) {
    updatedMessage.text = messagePatch.newText;
  }

  if (!isUndefined(messagePatch.newSubject)) {
    updatedMessage.subject = messagePatch.newSubject;
  }

  if (!isUndefined(messagePatch.newRecipients)) {
    updatedMessage.recipients = buildRecipientsFromVisual(messagePatch.newRecipients);
  }

  // An undefined canned answer id will not unset the value from the message doc,
  // only null will (this is not currently done in the client).
  if (!isUndefined(messagePatch.newCannedAnswerId)) {
    updatedMessage.cannedAnswerId = messagePatch.newCannedAnswerId;
  }

  /*
   * Quote.
   */

  if (!isUndefined(messagePatch.newQuoteHtml)) {
    updatedMessage.quoteHtml = messagePatch.newQuoteHtml;
  }

  if (!isUndefined(messagePatch.newIsQuoteIncluded)) {
    updatedMessage.isQuoteIncluded = messagePatch.newIsQuoteIncluded;
  }

  if (!isUndefined(messagePatch.newWasQuoteModified)) {
    updatedMessage.wasQuoteModified = messagePatch.newWasQuoteModified;
  }

  /*
   * Forward.
   */

  if (!isUndefined(messagePatch.newForwardHtml)) {
    updatedMessage.forwardHtml = messagePatch.newForwardHtml;
  }

  if (!isUndefined(messagePatch.newIsForwardIncluded)) {
    updatedMessage.isForwardIncluded = messagePatch.newIsForwardIncluded;
  }

  if (!isUndefined(messagePatch.newWasForwardModified)) {
    updatedMessage.wasForwardModified = messagePatch.newWasForwardModified;
  }

  /*
   * Signature.
   */

  if (!isUndefined(messagePatch.newSignatureId)) {
    updatedMessage.signatureId = messagePatch.newSignatureId;
  }

  if (!isUndefined(messagePatch.newSignatureHtml)) {
    updatedMessage.signatureHtml = messagePatch.newSignatureHtml;
  }

  if (!isUndefined(messagePatch.newIsSignatureIncluded)) {
    updatedMessage.isSignatureIncluded = messagePatch.newIsSignatureIncluded;
  }

  if (!isUndefined(messagePatch.newWasSignatureModified)) {
    updatedMessage.wasSignatureModified = messagePatch.newWasSignatureModified;
  }

  const signatureReset = !isUndefined(messagePatch.newChannelId) && maybeResetSignature(data, updatedMessage);
  if (signatureReset) {
    Object.assign(updatedMessage, signatureReset);
  }

  /*
   * Formatting.
   */

  if ('newFormat' in messagePatch) {
    updatedMessage.format = messagePatch.newFormat;
  }

  if (!isUndefined(messagePatch.newDefaultFontStyle)) {
    updatedMessage.defaultFontStyle = messagePatch.newDefaultFontStyle;
  }

  /*
   * Attachments.
   */

  const {addedAttachments, removedAttachmentCids} = messagePatch;

  if (!isUndefined(addedAttachments) && addedAttachments.length > 0) {
    updatedMessage.attachments = uniqBy([...updatedMessage.attachments, ...addedAttachments], (a) => a.cid);
  }

  if (!isUndefined(removedAttachmentCids) && removedAttachmentCids.length > 0) {
    updatedMessage.attachments = updatedMessage.attachments.filter(
      (a) => !removedAttachmentCids.includes(a.cid),
    );
  }

  updatedMessage.attachments = mergeInlineAttachments(updatedMessage);

  return updatedMessage;
}

function maybeResetSignature(
  data: DataState,
  message: DraftMessageModel,
): Partial<DraftMessageModel> | undefined {
  const {signatureId} = message;
  if (!signatureId) {
    return undefined;
  }

  const channel = data.channels.byId[message.fromChannelId];
  if (!channel) {
    return undefined;
  }

  const availableSignatures = selectSignaturesForChannel(data, channel);

  // The signature is still allowed.
  if (availableSignatures.some((s) => s.id === signatureId)) {
    return undefined;
  }

  // No signatures are allowed.
  if (availableSignatures.length === 0) {
    return {
      signatureId: undefined,
      isSignatureIncluded: false,
    };
  }

  // Change to the new default signature, but leave isSignatureIncluded unchanged.
  const newSignature = availableSignatures[0];
  return {
    signatureId: newSignature.id,
    signatureHtml: undefined,
    wasSignatureModified: false,
  };
}

/** Updates the message attachments based on the inline attachments referenced in the message content. */
function mergeInlineAttachments(message: DraftMessageModel): AttachmentModel[] {
  const attachedCids = new Set(message.attachments.map((a) => a.cid));

  const allHtml = [
    message.html,
    (message.isSignatureIncluded && message.signatureHtml) || '',
    (message.isQuoteIncluded && message.quoteHtml) || '',
    (message.isForwardIncluded && message.forwardHtml) || '',
  ].join('');
  const referencedAttachments = findInlineAttachmentsInHtml(allHtml);
  const referencedCids = new Set(referencedAttachments.map((a) => a.cid));

  // Re-attach inline attachments that were previously removed or that were copy/pasted from another draft.
  const newAttachments = _(referencedAttachments)
    .map(({cid, url, uid, name}): UploadedAttachmentModel | undefined => {
      if (attachedCids.has(cid) || !url || !uid) {
        return undefined;
      }

      // The server will fill in the actual attachment data in the save message response.
      return {
        linkId: undefined,
        uid,
        name: name || '',
        size: 0,
        contentType: '',
        visualContentType: AttachmentVisualContentTypesEnum.GENERIC,
        cid,
        giphyId: undefined,
        isInline: true,
        status: AttachmentStatusesEnum.UPLOADED,
        attachedAt: undefined,
        url,
        hasThumbnail: false,
      };
    })
    .compact()
    .value();

  return (
    _(message.attachments)
      // Remove inline attachments that are no longer referenced.
      .filter((attachment) => !attachment.isInline || referencedCids.has(attachment.cid))
      // Add newly referenced inline attachments.
      .concat(newAttachments)
      .value()
  );
}
